import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Title, useRedirect } from 'react-admin';
import { useParams } from 'react-router-dom';
import { LlmPromptResponse } from '../../../../interfaces/document';
import referralService from '../../../../services/referral';
import { formatDateString } from '../../../../util/dateFormat';
import { PageHeader } from '../../../common/PageHeader/PageHeader';
import '../../../common/tab-section.css';

const LlmPromptDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const redirect = useRedirect();
  const [prompt, setPrompt] = useState<LlmPromptResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPrompt = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const response = await referralService.fetchLlmPrompt(parseInt(id));
        if (response) {
          setPrompt(response);
        }
      } catch (error) {
        console.error('Error fetching LLM prompt:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPrompt();
  }, [id]);

  if (loading || !prompt) {
    return (
      <Box mt={4}>
        <Box className='tab-section'>
          {/* Header Skeleton */}
          <Box sx={{ mb: 3 }}>
            <Skeleton variant='text' width={200} height={32} sx={{ mb: 1 }} />
            <Skeleton variant='text' width={300} height={20} />
          </Box>

          {/* Basic Information Card Skeleton */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Skeleton variant='text' width='30%' height={24} sx={{ mb: 2 }} />
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={4}>
                {[1, 2, 3, 4, 5, 6].map((field) => (
                  <Grid item xs={12} md={4} key={field}>
                    <Skeleton variant='text' width='60%' height={16} sx={{ mb: 1 }} />
                    <Skeleton variant='text' width='80%' height={20} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Prompt Content Card Skeleton */}
          <Card>
            <CardContent>
              <Skeleton variant='text' width='20%' height={24} sx={{ mb: 2 }} />
              <Divider sx={{ mb: 2 }} />
              <Skeleton variant='rectangular' width='100%' height={400} />
            </CardContent>
          </Card>

          {/* Loading indicator */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant='body2' color='text.secondary' sx={{ fontSize: '12px' }}>
              Loading LLM prompt details...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box mt={4} className='tab-section'>
      <Title title={`LLM Prompt: ${prompt.name}`} />

      <PageHeader
        breadcrumbs={[
          { label: 'Documents', onClick: () => redirect('/documents/library') },
          { label: 'Settings', onClick: () => redirect('/documents/settings?tab=llm-prompts') },
          { label: 'LLM Prompts', onClick: () => redirect('/documents/settings?tab=llm-prompts') },
          { label: `${prompt.name}`, active: true },
        ]}
      />

      {/* Basic Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                ID
              </Typography>
              <Typography variant='body1' fontWeight='medium'>
                {prompt.id}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                Name
              </Typography>
              <Typography variant='body1' fontWeight='medium'>
                {prompt.name}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                Document Type
              </Typography>
              <Typography variant='body1'>{prompt.documentType}</Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                Version
              </Typography>
              <Typography variant='body1' fontFamily='monospace'>
                {prompt.version}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                Status
              </Typography>
              <Chip
                label={prompt.isActive ? 'Active' : 'Inactive'}
                color={prompt.isActive ? 'success' : 'default'}
                size='small'
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                Created
              </Typography>
              <Typography variant='body1'>
                {formatDateString(prompt.createdAt, 'MMM dd, yyyy HH:mm:ss')}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Prompt Content */}
      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            Prompt
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Paper
            variant='outlined'
            sx={{
              p: 2,
              maxHeight: 600,
              overflowY: 'auto',
              backgroundColor: 'grey.50',
            }}>
            <Typography
              variant='body2'
              component='pre'
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              }}>
              {prompt.content}
            </Typography>
          </Paper>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LlmPromptDetail;
